/**
 * Module-level access-token store. IN-MEMORY ONLY — never persisted to
 * `localStorage`, `sessionStorage`, or any cookie (Option A invariant). The
 * decoded JWT payload is cached alongside the token so consumers don't decode
 * on every read.
 *
 * The CSRF token captured from the refresh response body lives here too. It is
 * the saas-auth double-submit token; at this console's cross-origin position the
 * `journix_csrf` cookie may be unreadable from JS, so the refresh body is an
 * authoritative source. It is NEVER persisted and is cleared on sign-out.
 */
import { decodeJwtPayload, type JwtPayload } from '@/utils/jwt';

interface StoreState {
  token: string | null;
  expiresAt: string | null;
  claims: JwtPayload | null;
  csrfToken: string | null;
}

const state: StoreState = {
  token: null,
  expiresAt: null,
  claims: null,
  csrfToken: null,
};

let signOutCallback: ((expired: boolean) => void) | null = null;

/**
 * Bumped by every `signOut()` (DNKADM-0497). A refresh captures it when it starts
 * and its result is discarded if a sign-out happened meanwhile, so a refresh that
 * was queued on the cross-tab lock (or slow) can never sign a signed-out tab back
 * in. A counter, not the token: a cold-boot refresh starts with no token at all.
 */
let signOutGeneration = 0;

export function getSignOutGeneration(): number {
  return signOutGeneration;
}

export function getAccessToken(): string | null {
  return state.token;
}

export function getClaims(): JwtPayload | null {
  return state.claims;
}

export function getExpiresAt(): string | null {
  return state.expiresAt;
}

export function setAccessToken(token: string | null, expiresAt: string | null = null): void {
  state.token = token;
  state.expiresAt = expiresAt;
  state.claims = token ? decodeJwtPayload(token) : null;
}

export function getCsrfToken(): string | null {
  return state.csrfToken;
}

export function setCsrfToken(token: string | null): void {
  state.csrfToken = token;
}

export function registerSignOutCallback(cb: ((expired: boolean) => void) | null): void {
  signOutCallback = cb;
}

/**
 * Clear all in-memory auth state and fire the sign-out callback. `opts.expired`
 * distinguishes a runtime session-expiry (a bearer-surface `401` that could not
 * be silently refreshed) from a deliberate sign-out; it is forwarded to the
 * callback so React can show the "Session expired" interstitial on expiry only.
 * Default (no args / `expired` unset) = `false`, so every explicit-logout caller
 * is behaviorally unchanged.
 */
export function signOut(opts?: { expired?: boolean }): void {
  signOutGeneration += 1;
  state.token = null;
  state.expiresAt = null;
  state.claims = null;
  state.csrfToken = null;
  signOutCallback?.(opts?.expired ?? false);
}

/** Test seam — resets all in-memory state and the registered callback. */
export function __resetAuthStore(): void {
  state.token = null;
  state.expiresAt = null;
  state.claims = null;
  state.csrfToken = null;
  signOutCallback = null;
}
